import { useEffect, useRef, useState } from "react";
import { PersistentState, SessionState, AppState } from "../types";
import { SCHEMA_VERSION } from "../schema";
import { createSeedState } from "../seed";
import { nowIso } from "../workflow";
import { mergeEnterpriseAdminRecords } from "../utils/admin";
import { logger } from "../lib/logger";
import { summarizeStateDelta, trackUiAction } from "../lib/telemetry";
import { MonyawnStorage } from "../lib/storage";
import { syncStateToSupabase } from "../lib/persistence";

const STORAGE_KEY = "monyawn-platform-state-v1";

/**
 * Robust initial state loader that checks IndexedDB first, then LocalStorage (legacy), then Seed.
 */
export async function loadInitialState(): Promise<PersistentState> {
  // 1. Try IndexedDB (The new "Authority")
  const idbState = await MonyawnStorage.loadState();
  if (idbState) {
    logger.info("State loaded from IndexedDB");
    return sanitizeState(idbState as PersistentState);
  }

  // 2. Fallback to LocalStorage (Migration path)
  const rawState = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  if (rawState) {
    try {
      const parsed = JSON.parse(rawState) as PersistentState;
      logger.info("State migrated from LocalStorage to IndexedDB");
      const sanitized = sanitizeState(parsed);
      await MonyawnStorage.saveState(sanitized as any);
      return sanitized;
    } catch {
      // Continue to seed
    }
  }

  const seeded = createSeedState();
  return sanitizeState(seeded as PersistentState);
}

/**
 * Cleans up deprecated fields and ensures schema integrity.
 */
function sanitizeState(parsed: Partial<PersistentState>): PersistentState {
  const fallback = createSeedState();
  const accounts = parsed.accounts ?? fallback.accounts;

  const mergedAdminRecords = mergeEnterpriseAdminRecords(
    accounts,
    parsed.enterpriseControlProfiles,
    parsed.roleEntitlements,
  );

  const proof = parsed.proof ?? (parsed as any).artifacts ?? fallback.proof;
  const dms = parsed.dms ?? (parsed as any).correspondence ?? fallback.dms;
  const bags = parsed.bags ?? (parsed as any).commercialPostures ?? fallback.bags;
  const releaseReviews =
    parsed.releaseProofReviews ??
    (parsed as any).releaseArtifactReviews ??
    fallback.releaseProofReviews;

  const state: PersistentState = {
    ...fallback,
    ...parsed,
    accounts: parsed.accounts ?? fallback.accounts,
    users: parsed.users ?? fallback.users,
    opportunities: parsed.opportunities ?? fallback.opportunities,
    proof,
    candidateProfiles: parsed.candidateProfiles ?? fallback.candidateProfiles,
    bags,
    events: parsed.events ?? fallback.events,
    checkpoints: parsed.checkpoints ?? fallback.checkpoints,
    tasks: parsed.tasks ?? fallback.tasks,
    escalations: parsed.escalations ?? fallback.escalations,
    memos: parsed.memos ?? fallback.memos,
    dms,
    schemaVersion: parsed.schemaVersion ?? SCHEMA_VERSION,
    reportingSnapshots: parsed.reportingSnapshots ?? fallback.reportingSnapshots,
    candidateStories: parsed.candidateStories ?? fallback.candidateStories,
    sensitiveSupportProfiles: parsed.sensitiveSupportProfiles ?? fallback.sensitiveSupportProfiles,
    enterpriseControlProfiles: mergedAdminRecords.enterpriseControlProfiles,
    roleEntitlements: mergedAdminRecords.roleEntitlements,
    releaseProofReviews: releaseReviews.map((item: any) => ({
      ...item,
      pinned: item.pinned ?? false,
    })),
    outcomes: parsed.outcomes ?? fallback.outcomes,
    aiSettings: parsed.aiSettings ?? fallback.aiSettings,
    lastRecommendation: parsed.lastRecommendation ?? fallback.lastRecommendation,
    automationQueue: parsed.automationQueue ?? fallback.automationQueue,
    integrationHealthSnapshot:
      parsed.integrationHealthSnapshot ?? fallback.integrationHealthSnapshot,
    telemetryLog: parsed.telemetryLog ?? fallback.telemetryLog,
  } as PersistentState;

  return state;
}

export function useMonyawnCore(session: SessionState) {
  const [state, setState] = useState<PersistentState>(() => createSeedState() as PersistentState);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasPreInitMutationRef = useRef(false);

  // Async initialization from IndexedDB
  useEffect(() => {
    loadInitialState().then((loaded) => {
      setState((current) => (hasPreInitMutationRef.current ? current : loaded));
      setIsInitialized(true);
    });
  }, []);

  // Sync state to persistence layers
  useEffect(() => {
    if (!isInitialized) return;

    // 1. Primary: IndexedDB (Invincible)
    MonyawnStorage.saveState(state as any).catch((err) => {
      logger.error("Failed to save to IndexedDB", { err });
    });

    // 2. Legacy: LocalStorage (Shadow)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

    // 3. Cloud Shadow (Scalpel Sync)
    syncStateToSupabase(state as any).catch((err) => {
      logger.error("Failed to sync to Supabase", { err });
    });
  }, [state, isInitialized]);

  const patchState = (
    updater: (current: AppState) => PersistentState,
    actionLabel = "state_patch",
  ) => {
    if (!isInitialized) {
      hasPreInitMutationRef.current = true;
    }

    setState((current) => {
      const fused: AppState = {
        ...current,
        ...session,
      };
      const next = updater(fused);

      // Scalpel Sync: Calculate dirty IDs by comparing item references/values
      const dirtyIds = {
        opportunities: next.opportunities
          .filter((o, i) => o !== current.opportunities[i])
          .map((o) => o.opportunity_id),
        proof: next.proof.filter((p, i) => p !== current.proof[i]).map((p) => p.proof_id),
        dms: next.dms.filter((d, i) => d !== current.dms[i]).map((d) => d.dm_id),
        candidateStories: next.candidateStories
          .filter((s, i) => s !== current.candidateStories[i])
          .map((s) => s.story_id),
        tasks: next.tasks.filter((t, i) => t !== current.tasks[i]).map((t) => t.task_id),
        escalations: next.escalations
          .filter((e, i) => e !== current.escalations[i])
          .map((e) => e.opportunity_id),
      };

      const nextState: PersistentState = {
        ...next,
        dirtyIds: {
          opportunities: [
            ...new Set([...(current.dirtyIds?.opportunities || []), ...dirtyIds.opportunities]),
          ],
          proof: [...new Set([...(current.dirtyIds?.proof || []), ...dirtyIds.proof])],
          dms: [...new Set([...(current.dirtyIds?.dms || []), ...dirtyIds.dms])],
          candidateStories: [
            ...new Set([
              ...(current.dirtyIds?.candidateStories || []),
              ...dirtyIds.candidateStories,
            ]),
          ],
          tasks: [...new Set([...(current.dirtyIds?.tasks || []), ...dirtyIds.tasks])],
          escalations: [
            ...new Set([...(current.dirtyIds?.escalations || []), ...dirtyIds.escalations]),
          ],
        },
      };

      // Delta tracking still works on core data
      const delta = summarizeStateDelta(current as any, next as any);

      trackUiAction({
        at: nowIso(),
        action: actionLabel,
        mode: session.currentMode,
        selectedOpportunityId: session.selectedOpportunityId,
        delta,
      });

      logger.debug("patchState applied", {
        actionLabel,
        selectedOpportunityId: session.selectedOpportunityId,
        ...delta,
      });

      return nextState;
    });
  };

  const resetSeedState = () => {
    if (!isInitialized) {
      hasPreInitMutationRef.current = true;
    }

    const seeded = createSeedState() as PersistentState;
    setState(seeded);
  };

  return {
    state,
    setState,
    patchState,
    resetSeedState,
    isInitialized,
  };
}
